import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { incrementRecipeCount, decrementRecipeCount } from './userRecordsService';

export interface RecipeIngredient {
  name: string;
  quantity: number | string;
  unit: string; // 'g', 'kg', 'ml', 'L', 'xícara', 'colher', 'unidade', etc.
}

export interface Recipe {
  id: string;
  title: string;
  ingredients: RecipeIngredient[]; // Mudou de string[] para RecipeIngredient[]
  instructions: string;
  servings: number;
  prepTime: number;
  rating?: number;
  imageUrl?: string;
  videoUrl?: string;
  description?: string;
  category?: string;
  userId: string;
  eventsUsed?: number;
  sharedBy?: string; // Nome de quem compartilhou a receita
  sharedByUserId?: string; // ID do usuário que compartilhou
  isPublic?: boolean; // Publicada no livro de receitas público
  authorName?: string; // Nome de quem publicou (exibido no livro público)
  createdAt: Date;
  updatedAt: Date;
}

export const getUserRecipes = async (userId: string): Promise<Recipe[]> => {
  try {
    const recipesRef = collection(db, 'recipes');
    // Query otimizada com índice composto: (userId ASC, createdAt DESC)
    const q = query(
      recipesRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const recipes: Recipe[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Converter ingredientes antigos (string[]) para novo formato
      let ingredients: RecipeIngredient[] = [];
      if (Array.isArray(data.ingredients)) {
        if (data.ingredients.length > 0 && typeof data.ingredients[0] === 'string') {
          // Formato antigo - converter
          ingredients = data.ingredients.map((ing: string) => ({
            name: ing,
            quantity: 1,
            unit: 'unidade'
          }));
        } else {
          ingredients = data.ingredients as RecipeIngredient[];
        }
      }

      recipes.push({
        id: doc.id,
        title: data.title || '',
        ingredients,
        instructions: data.instructions || '',
        servings: data.servings || 1,
        prepTime: data.prepTime || 0,
        rating: data.rating || 0,
        imageUrl: data.imageUrl,
        videoUrl: data.videoUrl,
        description: data.description,
        category: data.category,
        userId: data.userId,
        eventsUsed: data.eventsUsed || 0,
        sharedBy: data.sharedBy,
        sharedByUserId: data.sharedByUserId,
        isPublic: data.isPublic === true,
        authorName: data.authorName,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      });
    });

    return recipes;
  } catch (error) {
    console.error('Erro ao buscar receitas:', error);
    throw error;
  }
};

export const getRecipeById = async (userId: string, recipeId: string): Promise<Recipe | null> => {
  try {
    const recipeRef = doc(db, 'recipes', recipeId);
    const recipeDoc = await getDoc(recipeRef);

    if (!recipeDoc.exists()) {
      return null;
    }

    const data = recipeDoc.data();

    if (data.userId !== userId) {
      throw new Error('Não autorizado');
    }

    // Converter ingredientes antigos
    let ingredients: RecipeIngredient[] = [];
    if (Array.isArray(data.ingredients)) {
      if (data.ingredients.length > 0 && typeof data.ingredients[0] === 'string') {
        ingredients = data.ingredients.map((ing: string) => ({
          name: ing,
          quantity: 1,
          unit: 'unidade'
        }));
      } else {
        ingredients = data.ingredients as RecipeIngredient[];
      }
    }

    return {
      id: recipeDoc.id,
      title: data.title || '',
      ingredients,
      instructions: data.instructions || '',
      servings: data.servings || 1,
      prepTime: data.prepTime || 0,
      rating: data.rating || 0,
      imageUrl: data.imageUrl,
      videoUrl: data.videoUrl,
      description: data.description,
      category: data.category,
      userId: data.userId,
      eventsUsed: data.eventsUsed || 0,
      sharedBy: data.sharedBy,
      sharedByUserId: data.sharedByUserId,
      isPublic: data.isPublic === true,
      authorName: data.authorName,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  } catch (error) {
    console.error('Erro ao buscar receita:', error);
    throw error;
  }
};

const mapRecipeDoc = (id: string, data: any): Recipe => {
  let ingredients: RecipeIngredient[] = [];
  if (Array.isArray(data.ingredients)) {
    ingredients = data.ingredients.length > 0 && typeof data.ingredients[0] === 'string'
      ? data.ingredients.map((ing: string) => ({ name: ing, quantity: 1, unit: 'unidade' }))
      : (data.ingredients as RecipeIngredient[]);
  }
  return {
    id,
    title: data.title || '',
    ingredients,
    instructions: data.instructions || '',
    servings: data.servings || 1,
    prepTime: data.prepTime || 0,
    rating: data.rating || 0,
    imageUrl: data.imageUrl,
    videoUrl: data.videoUrl,
    description: data.description,
    category: data.category,
    userId: data.userId,
    eventsUsed: data.eventsUsed || 0,
    sharedBy: data.sharedBy,
    sharedByUserId: data.sharedByUserId,
    isPublic: data.isPublic === true,
    authorName: data.authorName,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
};

export const getPublicRecipes = async (limitCount = 100): Promise<Recipe[]> => {
  const recipesRef = collection(db, 'recipes');
  const q = query(recipesRef, where('isPublic', '==', true), orderBy('createdAt', 'desc'), limit(limitCount));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => mapRecipeDoc(d.id, d.data()));
};

export const getPublicRecipeById = async (recipeId: string): Promise<Recipe | null> => {
  const recipeDoc = await getDoc(doc(db, 'recipes', recipeId));
  if (!recipeDoc.exists()) return null;
  const data = recipeDoc.data();
  if (data.isPublic !== true) return null;
  return mapRecipeDoc(recipeDoc.id, data);
};

export const setRecipePublic = async (
  userId: string,
  recipeId: string,
  isPublic: boolean,
  authorName?: string
): Promise<void> => {
  await updateRecipe(userId, recipeId, { isPublic, ...(isPublic && authorName ? { authorName } : {}) });
};

export const copyPublicRecipe = async (userId: string, recipe: Recipe): Promise<string> => {
  return createRecipe(userId, {
    title: recipe.title,
    ingredients: recipe.ingredients,
    instructions: recipe.instructions,
    servings: recipe.servings,
    prepTime: recipe.prepTime,
    imageUrl: recipe.imageUrl || '',
    videoUrl: recipe.videoUrl || '',
    description: recipe.description || '',
    category: recipe.category || '',
    userId,
    isPublic: false,
    sharedBy: recipe.authorName || recipe.sharedBy || '',
    sharedByUserId: recipe.userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  } as Omit<Recipe, 'id'>);
};

export const createRecipe = async (
  userId: string,
  recipeData: Omit<Recipe, 'id'>
): Promise<string> => {
  try {
    const recipesRef = collection(db, 'recipes');

    const newRecipe = {
      ...recipeData,
      userId,
      eventsUsed: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(recipesRef, newRecipe);
    await incrementRecipeCount(userId);

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar receita:', error);
    throw error;
  }
};

export const updateRecipe = async (
  userId: string,
  recipeId: string,
  updates: Partial<Recipe>
): Promise<void> => {
  try {
    const recipeRef = doc(db, 'recipes', recipeId);
    const recipeDoc = await getDoc(recipeRef);

    if (!recipeDoc.exists()) {
      throw new Error('Receita não encontrada');
    }

    if (recipeDoc.data().userId !== userId) {
      throw new Error('Não autorizado');
    }

    const { id, userId: _, createdAt, ...validUpdates } = updates as any;

    await updateDoc(recipeRef, {
      ...validUpdates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Erro ao atualizar receita:', error);
    throw error;
  }
};

export const deleteRecipe = async (userId: string, recipeId: string): Promise<void> => {
  try {
    const recipeRef = doc(db, 'recipes', recipeId);
    const recipeDoc = await getDoc(recipeRef);

    if (!recipeDoc.exists()) {
      throw new Error('Receita não encontrada');
    }

    if (recipeDoc.data().userId !== userId) {
      throw new Error('Não autorizado');
    }

    await deleteDoc(recipeRef);
    await decrementRecipeCount(userId);
  } catch (error) {
    console.error('Erro ao deletar receita:', error);
    throw error;
  }
};

// Calculadora de porções (ajusta quantidades proporcionalmente)
export const calculatePortions = (
  recipe: Recipe,
  newServings: number
): RecipeIngredient[] => {
  const multiplier = newServings / recipe.servings;
  
  return recipe.ingredients.map(ingredient => ({
    ...ingredient,
    quantity: typeof ingredient.quantity === 'number'
      ? Math.round(ingredient.quantity * multiplier * 100) / 100
      : ingredient.quantity
  }));
};
export const setWholeBookPublic = async (
  userId: string,
  isPublic: boolean,
  authorName?: string
): Promise<number> => {
  await setDoc(doc(db, 'users', userId), { publicRecipeBook: isPublic }, { merge: true });

  const snap = await getDocs(query(collection(db, 'recipes'), where('userId', '==', userId)));
  let batch = writeBatch(db);
  let count = 0;
  let pending = 0;

  for (const d of snap.docs) {
    const update: Record<string, unknown> = { isPublic, updatedAt: Timestamp.now() };
    if (isPublic && authorName) update.authorName = authorName;
    batch.update(d.ref, update);
    count++;
    pending++;
    if (pending === 400) {
      await batch.commit();
      batch = writeBatch(db);
      pending = 0;
    }
  }
  if (pending > 0) await batch.commit();

  return count;
};

export const isBookPublic = async (userId: string): Promise<boolean> => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() && snap.data()?.publicRecipeBook === true;
};

export interface PublicBookAuthor {
  userId: string;
  name: string;
}

export const getPublicBookAuthors = async (): Promise<PublicBookAuthor[]> => {
  const snap = await getDocs(query(collection(db, 'users'), where('publicRecipeBook', '==', true)));
  return snap.docs.map((d) => {
    const data = d.data();
    const name = `${data.name || ''} ${data.lastName || ''}`.trim();
    return { userId: d.id, name: name || 'Anônimo' };
  });
};

export const getPublicRecipesByAuthor = async (authorUserId: string): Promise<Recipe[]> => {
  const snap = await getDocs(query(collection(db, 'recipes'), where('userId', '==', authorUserId)));
  return snap.docs
    .map((d) => mapRecipeDoc(d.id, d.data()))
    .filter((r) => r.isPublic)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
};
